import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';
import Breadcrumb from '@/components/base/Breadcrumb';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentCategory = categories.find(c => c.slug === slug) || categories[0];
  const [activeCategory, setActiveCategory] = useState(currentCategory.slug);

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
    }
  }, [slug]);

  const filteredArticles = getArticlesByCategory(activeCategory);
  const activeCategoryData = categories.find(c => c.slug === activeCategory);

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: activeCategory === 'all' ? '全部爆料' : activeCategoryData?.name || '分类' },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <div className="pt-16">
        <Breadcrumb items={breadcrumbItems} />

        <section className="px-4 md:px-6 py-8">
          <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-2">
            {activeCategory === 'all' ? '全部爆料' : activeCategoryData?.name}
          </h1>
          <p className="text-sm text-foreground-500 mb-6">
            {activeCategoryData?.description || '浏览所有分类的爆料内容'}
          </p>

          <div className="flex items-center gap-2 overflow-x-auto pb-3 mb-8 flex-wrap">
            {categories.map(cat => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                onClick={() => setActiveCategory(cat.slug)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm whitespace-nowrap transition-all duration-200 ${
                  activeCategory === cat.slug
                    ? 'bg-primary-500 text-background-50 font-medium'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                <i className={`${cat.icon} text-xs`}></i>
                {cat.name}
              </Link>
            ))}
          </div>

          {filteredArticles.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                <i className="ri-inbox-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">该分类暂无爆料内容</p>
              <Link
                to="/category/all"
                className="inline-flex items-center gap-1 mt-4 text-sm text-primary-500 hover:text-primary-600"
              >
                查看全部爆料 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {filteredArticles.map(article => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="group bg-background-100 rounded-lg overflow-hidden transition-all duration-300 hover:scale-[1.02]"
                >
                  <div className="relative aspect-[16/9] overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                    {article.isExclusive && (
                      <span className="absolute top-3 left-3 px-2 py-0.5 text-xs bg-accent-500 text-background-50 rounded-full whitespace-nowrap">
                        独家
                      </span>
                    )}
                    {article.isVideo && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-12 h-12 flex items-center justify-center rounded-full bg-background-50/90 text-foreground-950">
                          <i className="ri-play-fill text-xl ml-0.5"></i>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs text-primary-500 font-medium">
                        {categories.find(c => c.slug === article.category)?.name}
                      </span>
                      <span className="text-xs text-foreground-400">{article.publishDate}</span>
                    </div>
                    <h3 className="text-sm md:text-base font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-500 transition-colors duration-200">
                      {article.title}
                    </h3>
                    <p className="text-xs text-foreground-500 line-clamp-2 mb-3">{article.excerpt}</p>
                    <div className="flex items-center gap-3 text-xs text-foreground-400">
                      <span className="flex items-center gap-1">
                        <i className="ri-eye-line"></i> {(article.views / 10000).toFixed(1)}万
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-chat-3-line"></i> {article.comments}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}

          {filteredArticles.length > 0 && (
            <div className="flex items-center justify-center gap-2 mt-10">
              <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-500 text-background-50 text-sm font-medium">1</button>
              <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600 hover:bg-background-200 text-sm">2</button>
              <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600 hover:bg-background-200 text-sm">3</button>
              <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600 hover:bg-background-200">
                <i className="ri-arrow-right-s-line"></i>
              </button>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}